import { Alert, Empty, Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import { AuthGuard } from '~/app/auth/guard/AuthGuard';
import { ErrorBox } from '~/app/components/common/Error/ErrorBox';
import { Loader } from '~/app/components/common/Loader/Loader';
import { PlatformSettingsApiError } from '~/app/services/settings/platform-settings.service';
import { CategoryNavigation } from './components/CategoryNavigation/CategoryNavigation';
import { ConfirmSettingDialog } from './components/ConfirmSettingDialog/ConfirmSettingDialog';
import { SettingHistoryDrawer } from './components/SettingHistoryDrawer/SettingHistoryDrawer';
import { SettingsHeader } from './components/SettingsHeader/SettingsHeader';
import { SettingsSection } from './components/SettingsSection/SettingsSection';
import { usePlatformSettings } from './hooks/usePlatformSettings';
import { resolveSettingsErrorMessageKey } from './utils';

function PlatformSettingsContent() {
  const { t } = useTranslation('settings');
  const page = usePlatformSettings();

  if (page.isLoading) return <Loader />;
  if (page.listError) {
    const status =
      page.listError instanceof PlatformSettingsApiError
        ? page.listError.status
        : undefined;
    return (
      <ErrorBox
        title={t('errors.loadTitle')}
        description={t(resolveSettingsErrorMessageKey(status))}
      />
    );
  }

  return (
    <>
      <Flex vertical gap={24} className="app-page platform-settings-page">
        <SettingsHeader
          search={page.search}
          dirtyCount={page.dirtyCount}
          isRefreshing={page.isRefreshing}
          onSearchChange={page.setSearch}
          onDiscardAll={page.onDiscardAll}
          onRefresh={() => void page.onRefresh()}
        />
        <Alert showIcon type="info" message={t('page.authorityNotice')} />
        <CategoryNavigation
          categories={page.categories}
          value={page.category}
          onChange={page.setCategory}
        />
        {page.groups.length > 0 ? (
          page.groups.map((group) => (
            <SettingsSection
              key={group.category}
              category={group.category}
              settings={group.settings}
              drafts={page.drafts}
              onDraftChange={page.onDraftChange}
              onApply={page.onRequestApply}
              onHistory={page.setHistoryKey}
            />
          ))
        ) : (
          <Empty description={t('page.empty')} />
        )}
      </Flex>

      <ConfirmSettingDialog
        setting={page.pendingSetting}
        draftValue={
          page.pendingSetting ? page.drafts[page.pendingSetting.key] : undefined
        }
        reason={page.reason}
        loading={page.isUpdating}
        onReasonChange={page.setReason}
        onCancel={page.onCancelApply}
        onConfirm={page.onConfirmApply}
      />
      <SettingHistoryDrawer
        settingKey={page.historyKey}
        onClose={() => page.setHistoryKey(null)}
      />
    </>
  );
}

export function PlatformSettingsPage() {
  return (
    <AuthGuard requiredRole={SystemRoleName.SUPER_ADMIN}>
      <PlatformSettingsContent />
    </AuthGuard>
  );
}
